import {
  Badge,
  Box,
  Flex,
  Grid,
  Heading,
  Icon,
  Stack,
  Text,
} from '@chakra-ui/react';
import {
  UsersIcon,
  UserGroupIcon,
  TicketIcon,
  PencilSquareIcon,
  CommandLineIcon,
  Squares2X2Icon,
  RectangleStackIcon,
  ShieldCheckIcon,
  KeyIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import { NavLink, useParams } from 'react-router-dom';
import { Avatar } from '@/components/ui/avatar';
import useWorkspace from '@/lib/hooks/use-workspace';
import useIsCurrentUser from '@/lib/hooks/use-is-current-user';
import useIsInAdminGroup from '@/lib/hooks/use-is-in-admin-group';
import useFeatureFlag from '@/lib/hooks/use-feature-flag';

interface NavItemProps {
  text: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  to: string;
  last?: boolean;
}

const NavItem = ({ text, icon, to, last }: NavItemProps) => {
  return (
    <NavLink to={to} style={{ display: 'block' }}>
      <Flex
        direction='row'
        align='center'
        gap={3}
        px={5}
        py={4}
        borderBottomWidth={last ? 0 : 1}
        borderColor='border'
        _hover={{ bg: 'bg.subtle' }}
        cursor='pointer'
        transition='background 0.15s'
      >
        <Flex
          w={8}
          h={8}
          align='center'
          justify='center'
          borderRadius='full'
          bg='brand.soft'
          flexShrink={0}
        >
          <Icon as={icon} w={4} h={4} color='brand.fg' />
        </Flex>
        <Text flex={1} fontSize='ui' fontWeight='500' color='fg.strong'>
          {text}
        </Text>
        <Icon as={ChevronRightIcon} w={4} h={4} color='fg.muted' />
      </Flex>
    </NavLink>
  );
};

export default function Settings() {
  const { workspaceId } = useParams();
  const { workspace } = useWorkspace();
  const currentUser = useIsCurrentUser();
  const isAdmin = useIsInAdminGroup();
  const ssoEnabled = useFeatureFlag('sso');
  // Groups exist only as course audiences, so they hide with Courses.
  const coursesEnabled = useFeatureFlag('courses');

  const ownerName =
    currentUser
      ? `${currentUser.first_name} ${currentUser.last_name}`.trim()
      : '';

  const navRows: NavItemProps[] = [
    { text: 'Update this workspace', icon: PencilSquareIcon, to: `/${workspaceId}/account/edit` },
    { text: 'Users', icon: UsersIcon, to: `/${workspaceId}/users` },
    ...(isAdmin && coursesEnabled
      ? [{ text: 'Groups', icon: UserGroupIcon, to: `/${workspaceId}/groups` }]
      : []),
    ...(ssoEnabled
      ? [{ text: 'Single sign-on', icon: ShieldCheckIcon, to: `/${workspaceId}/account/sso` }]
      : []),
    { text: 'Billing', icon: TicketIcon, to: `/${workspaceId}/billing` },
    { text: 'Fields', icon: CommandLineIcon, to: `/${workspaceId}/fields` },
    { text: 'Content libraries', icon: Squares2X2Icon, to: `/${workspaceId}/content-types` },
    { text: 'Content collections', icon: RectangleStackIcon, to: `/${workspaceId}/collection-types` },
    ...(isAdmin
      ? [{ text: 'Developer (API keys)', icon: KeyIcon, to: `/${workspaceId}/developer/keys` }]
      : []),
  ];

  return (
    <Box mx='auto' width={{ md: '90%' }} maxW='6xl' px={{ base: 4, md: 0 }} py={6}>
      <Heading as='h1' size='3xl' mb={6}>
        Settings
      </Heading>

      <Grid
        gridTemplateColumns={{ base: '1fr', md: '1.3fr 0.9fr' }}
        gap={6}
        alignItems='start'
      >
        {/* Left column — navigation rows */}
        <Box
          bg='bg.panel'
          border='1px solid'
          borderColor='border'
          borderRadius='lg'
          shadow='sm'
          overflow='hidden'
        >
          {navRows.map((row, i) => (
            <NavItem
              key={row.to}
              text={row.text}
              icon={row.icon}
              to={row.to}
              last={i === navRows.length - 1}
            />
          ))}
        </Box>

        {/* Right column — workspace owner */}
        <Stack gap={4}>
          {/* Workspace owner card */}
          <Box
            bg='bg.panel'
            border='1px solid'
            borderColor='border'
            borderRadius='lg'
            shadow='sm'
            p={5}
          >
            <Text textStyle='eyebrow' mb={3}>
              Signed in as
            </Text>
            <Flex align='center' gap={3} mb={4}>
              <Avatar name={ownerName} size='sm' flexShrink={0} />
              <Box>
                <Text fontSize='ui' fontWeight='600' color='fg.strong'>
                  {ownerName || workspace?.name}
                </Text>
                <Text fontSize='sm' color='fg.muted'>
                  Admin
                </Text>
              </Box>
            </Flex>
            <Flex align='center' gap={3}>
              <Box
                w={7}
                h={7}
                borderRadius='full'
                bg={workspace?.primary_color || 'brand.solid'}
                border='1px solid'
                borderColor='border'
                flexShrink={0}
              />
              <Text fontSize='sm' color='fg.muted'>
                Workspace primary{' '}
                <Badge size='xs' variant='subtle' ml={1}>
                  Themeable
                </Badge>
              </Text>
            </Flex>
          </Box>
        </Stack>
      </Grid>
    </Box>
  );
}
